// client/src/pages/seo/MultilingualMenuForRestaurantsEn.jsx
import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import SeoHeader from "../../components/seo/SeoHeader.jsx";
import SeoContactSection from "../../components/seo/SeoContactSection.jsx";
import SeoWorkSteps from "../../components/seo/SeoWorkSteps.jsx";
import CountryBanner from "../../components/CountryBanner.jsx";

const navItems = [
  { id: "What-mean", label: "What is a multilingual menu for restaurants?" },
  { id: "foreign-language", label: "Foreign language menus for restaurants: a simpler way to serve international guests" },
  { id: "checklist", label: "Does your restaurant need a multilingual menu?" },
  { id: "Why-important", label: "Why is a multilingual menu important?" },
  { id: "reduces", label: "Language reduces uncertainty before ordering" },
  { id: "languages", label: "Which languages should a restaurant offer?" },
  { id: "automatic", label: "Why automatic translation is not always enough?" },
  { id: "qr", label: "Why QR code menus make multilingual menus easier" },
  { id: "mistakes", label: "Common multilingual menu mistakes" },
  { id: "bestplace", label: "How Best Place helps you launch a QR menu" },
  { id: "faq", label: "Frequently asked questions" },
];

import guest1 from "../../img/seo/important_1.png";
import guest2 from "../../img/seo/important_2.png";
import guest3 from "../../img/seo/important_3.png";
import guest4 from "../../img/seo/important_4.png";
import guest5 from "../../img/seo/important_5.png";

const acquaintancePhotos = [
  {
    src: guest1,
    alt: "Guest scanning a restaurant QR menu",
  },
  {
    src: guest2,
    alt: "Digital restaurant menu on a smartphone",
  },
  {
    src: guest3,
    alt: "Restaurant guest choosing a dish online",
  },
  {
    src: guest4,
    alt: "Restaurant owner editing the menu",
  },
  {
    src: guest5,
    alt: "Online ordering and payment in a restaurant",
  },
];

const CALLBACK_API_ORIGIN =
  typeof window !== "undefined" &&
  (window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1")
    ? "http://localhost:3030"
    : "";

const checklist = [
  "Your restaurant regularly serves international guests.",
  "Tourists often ask for English or another language.",
  "Your menu includes regional dishes that are hard to explain.",
  "Guests often ask about ingredients, allergens, or preparation.",
  "You want to reduce misunderstandings when guests order.",
  "You want your menu to be clearer online and through QR code access.",
  "Your restaurant is in a tourist city or near hotels.",
  "You want to offer multiple languages without printing several paper menus.",
  "You want to look more professional for international guests.",
];

const faqLinks = [
  {
    question: "Does a guest need to install an app to open a QR menu?",
    answer:
      "No. A good QR menu should open directly in the smartphone browser. This keeps the experience simple and removes one of the biggest barriers for guests.",
    linkText: "Read more about QR menus without an app",
    to: "/en/qr-code-menu-without-app",
  },
  {
    question: "What is the best way to translate a restaurant menu?",
    answer:
      "It depends on how often your menu changes and how many international guests you serve. Paper menus can work for stable menus. AI can help with a first draft. A multilingual QR menu is usually more flexible when you need several languages and regular updates.",
    linkText: "Compare restaurant menu translation options",
    to: "/en/menu-translation-for-restaurants",
  },
  {
    question: "Can I test a digital menu for free before deciding?",
    answer:
      "Yes. For many restaurants, the smartest first step is not a large technical project, but a simple test: create a menu, scan the QR code, and see how it feels on a real phone.",
    linkText: "Read more about testing a free digital menu",
    to: "/en/free-digital-menu-test",
  },
  {
    question: "Can I update my menu online without reprinting it?",
    answer:
      "Yes. This is one of the most practical reasons to use a digital menu. Prices, unavailable dishes, daily offers, and photos can be changed online instead of rebuilding a PDF or printing a new menu.",
    linkText: "Read more about managing a menu online",
    to: "/en/manage-menu-online-for-restaurants",
  },
  {
    question: "Do pictures in a digital menu really matter?",
    answer:
      "They can matter a lot, especially for dishes, drinks, sets, desserts, and seasonal offers. A guest often understands an offer faster when it is shown clearly instead of only described.",
    linkText: "Read more about menus with pictures",
    to: "/en/menu-with-pictures",
  },
  {
    question: "Can I use a QR code on table tents?",
    answer:
      "Yes. Table tents are one of the most natural places for a QR menu because the guest sees the code exactly when they are ready to choose something.",
    linkText: "Read more about QR code table tents",
    to: "/en/restaurant-qr-code-table-tent",
  },
  {
    question: "Can a QR menu work together with online table reservation?",
    answer:
      "Yes. A digital menu can be part of a wider restaurant system: menu, table reservation, photos, current offers, and later even ordering workflows.",
    linkText: "Read more about online table reservation",
    to: "/en/online-table-reservation-for-restaurants",
  },
  {
    question: "Is a digital menu useful for a pizzeria?",
    answer:
      "Yes, especially when you need to show sizes, toppings, extras, sets, lunch offers, or seasonal promotions in a clear way.",
    linkText: "Read more about digital menus for pizzerias",
    to: "/en/digital-menu-pizzeria",
  },
];

function getResult(score) {
  if (score <= 3) {
    return {
      title: "Multilingual menus may not be a priority yet",
      text:
        "If your guests are almost entirely local and your menu is simple, a single-language menu may be enough for now.",
      tone: "low",
    };
  }

  if (score <= 6) {
    return {
      title: "A second language is worth testing",
      text:
        "Your restaurant could benefit from adding another language, especially if guests often ask about dishes, ingredients, or prices.",
      tone: "medium",
    };
  }

  return {
    title: "A multilingual menu can be very useful",
    text:
      "Several points suggest that your restaurant could benefit from a multilingual menu: tourists, clearer ordering, fewer questions, and a more professional impression.",
      tone: "high",
    };
  };




function Section({ id, number, title, children }) {
  return (
    <section id={id} className="multiMenuArticle__section">
      <div className="multiMenuArticle__sectionNumber">{number}</div>
      <h2>{title}</h2>
      {children}
    </section>
  );
}

export default function MultilingualMenuForRestaurantsEn() {
  const [checked, setChecked] = useState([]);

  const score = checked.length;
  const result = useMemo(() => getResult(score), [score]);

  const toggle = (item) => {
    setChecked((prev) =>
      prev.includes(item) ? prev.filter((x) => x !== item) : [...prev, item]
    );
  };

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  return (
    <main className="qrArticle">
      <SeoHeader
        lang="en"
        langLinks={{
          en: "/en/multilingual-menu-for-restaurants",
          de: "/de/mehrsprachige-speisekarte",
          no: "/no/flerspraklig-meny-for-restauranter",
        }}
      />

      <section id="ready" className="qrArticle__hero">
        <div className="qrArticle__heroInner">

          <h1 className="qrArticle__heroTitle">
            <span>Multilingual menu for restaurants: easier to understand, easier to order</span>
          </h1>

          <div className="qrArticle__topsection">
            <div className="qrArticle__topsection--img-wrapper">
              <div className="qrArticle__topsection--img"></div>
            </div>
            <div className="qrArticle__topsection--text-wrapper">
              <p>
                A multilingual menu is more than a translation. It helps guests
                understand dishes more confidently, reduces questions, and makes
                ordering easier.
              </p>

              <p>
                For international guests, tourists, or regional specialties,
                language can influence whether a guest confidently tries something
                new — or chooses only what they already know.
              </p>
            </div>
          </div>
        </div>
      </section>

      <div className="countryBannerBlock">
        <CountryBanner
          reverse
          ariaLabel={"Countries and languages"}
        />
      </div>
      

      <SeoContactSection
        lang="en"
        apiOrigin={CALLBACK_API_ORIGIN}
        title="Do you need help creating a multilingual menu?"
      />


      <section className="articls">
        
        <h2>Content of the article</h2>

        <nav className="qrArticle__nav" aria-label="Article navigation">
        <ol className="qrArticle__navList">
          {navItems.map((item, index) => (
            <li key={item.id} className="qrArticle__navItem">
              <button
                type="button"
                className="qrArticle__navButton"
                onClick={() => scrollTo(item.id)}
              >
                <span className="qrArticle__navNumber">
                  {String(index + 1).padStart(2, "0")}
                </span>

                <span className="qrArticle__navLabel">
                  {item.label}
                </span>
              </button>
            </li>
          ))}
        </ol>
      </nav>

      </section>



      <article className="">
        <section id="What-mean" className="acquaintance">
          <h2>What is a multilingual menu for restaurants?</h2>
          
            <p>
              The multilingual menu displays dish titles, descriptions, categories, 
              and important information in multiple languages. Currently, the local 
              language is used as the primary version, with English as the secondary 
              language.
            </p>

            <p>
              Technically, it works like this: the guest scans a QR code, and a web app 
              for the country they're currently in opens. Selecting a different language
               is always possible. This is actually a big advantage over traditional menus, 
              as this is only possible in a digital format.
            </p>

            <p>
              In that case, I have a ready-made solution for you. In the next 
              section, you can answer a short checklist and see whether this 
              kind of digital menu is useful for your restaurant.
            </p>

          

        </section>


        <section id="foreign-language" className="acquaintance">
          <h2>Foreign language menus for restaurants: a simpler way to serve international guests</h2>
          
            <p>
              Foreign language menus are often important before a guest even 
              sits at the table. Many tourists and international guests check 
              a restaurant online first. If they can see that the menu is 
              available in a language they understand, the restaurant feels 
              easier, safer, and more welcoming. For some guests, this can 
              influence which restaurant they choose.
            </p>

            <p>
              That is why foreign language menus should not be complicated 
              for the restaurant owner. You do not need to create separate PDFs, 
              redesign printed menus, or manually rebuild the same menu again and 
              again. With a digital multilingual menu, the process can be much 
              simpler: the restaurant prepares one Excel file with dish names, 
              descriptions, prices, categories, and the needed language versions. 
              After uploading it, the menu can be turned into a ready-to-use QR menu with the languages your guests need.
            </p>

            <p>
              This gives the restaurant one clear menu structure and several language 
              versions inside it. Guests can open the menu on their phone, choose 
              their language, understand dishes faster, and order with more 
              confidence. For the restaurant team, it means fewer translation 
              questions, easier updates, and a better experience for foreign 
              guests without extra printed menus.
            </p>
        </section>
        
        
        <section id="checklist" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>Does your restaurant need a multilingual menu?</h2>

            <p>
              This short check helps you decide whether a second or third language
              would be useful for your restaurant.
            </p>

            <div className="qrArticle__checkGrid">
              {checklist.map((item) => (
                <label key={item} className="qrArticle__checkItem">
                  <input
                    type="checkbox"
                    checked={checked.includes(item)}
                    onChange={() => toggle(item)}
                  />
                  <span className="text">{item}</span>
                </label>
              ))}
            </div>

            <div className={`qrArticle__result qrArticle__result--${result.tone}`}>
              <div className="qrArticle__score">
                {score} / {checklist.length}
              </div>

              <div>
                <strong>{result.title}</strong>
                <p>{result.text}</p>
              </div>
            </div>
          </div>
        </section>


        <section id="Why-important" className="acquaintance">
          <h2>Why is a multilingual menu important?</h2>

          <p>
            In tourist cities and international business regions, foreign guests can make
            up a significant part of hospitality demand. For example, the German National
            Tourist Board reported 85.3 million overnight stays by foreign visitors in
            Germany in 2024. In some city regions, the share can be even more visible:
            a DEHOGA / IW Consult report noted that 41.2% of guest overnight stays in
            Düsseldorf were made by foreign guests. For restaurants, this means that
            international visitors are not a rare exception. They can be a normal part
            of daily business.
          </p>

          <div className="picture__slider" aria-label="Restaurant QR menu benefits">
            {acquaintancePhotos.map((photo, index) => (
              <div className="picture__slide" key={index}>
                <img src={photo.src} alt={photo.alt} />
              </div>
            ))}
          </div>

          <p>
            For restaurants, this means international guests are not a rare
            exception. Depending on the location, they can be a normal part of
            everyday business.
          </p>

          <p>
            The National Restaurant Association reports that many operators
            see technology as a competitive advantage when it improves
            convenience, speed and operations. A multilingual menu can support
            exactly that.
          </p>

        </section>

        <section id="reduces" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>Language reduces uncertainty before ordering</h2>
          <p>
            When guests do not understand a dish, they often order more
            cautiously. They choose something familiar, ask several questions, or
            skip add-ons. A clear menu can reduce that uncertainty.
          </p>

          <div className="multiMenuArticle__comparison">
            <div>
              <h3>One language only</h3>
              <ul className="qrArticle__block-li">
                <li className="text__seo-li">guests may need to guess or ask</li>
                <li className="text__seo-li">regional terms remain unclear</li>
                <li className="text__seo-li">allergens and ingredients feel uncertain</li>
                <li className="text__seo-li">add-ons can be overlooked</li>
              </ul>
            </div>

            <div className="multiMenuArticle__comparisonHighlight">
              <h3>Multilingual menu</h3>
              <ul className="qrArticle__block-li">
                <li className="text__seo-li">more confidence when ordering</li>
                <li className="text__seo-li">fewer questions</li>
                <li className="text__seo-li">better explanation of local dishes</li>
                <li className="text__seo-li">more professional impression for international guests</li>
              </ul>
            </div>
          </div>

          <p>
            The goal is not to make every menu text long. The goal is to provide
            enough clarity for the guest to feel safe choosing.
          </p>
          </div>
        </section>


        <section id="languages" className="acquaintance">
          <h2>Which languages should a restaurant offer?</h2>
            <p>
            For many restaurants, the best start is simple: the local language
            and English. After that, the right languages depend on the location.
          </p>

          <div className="multiMenuArticle__cardGrid">
            <div>
              <strong>English</strong>
              <p>often the most useful first additional language</p>
            </div>
            <div>
              <strong>French</strong>
              <p>useful in regions with French-speaking guests</p>
            </div>
            <div>
              <strong>Italian</strong>
              <p>interesting for tourist areas or Italian cuisine</p>
            </div>
            <div>
              <strong>Spanish</strong>
              <p>helpful in international cities and tourist areas</p>
            </div>
            <div>
              <strong>Dutch</strong>
              <p>relevant in tourist regions with Dutch guests</p>
            </div>
            <div>
              <strong>Polish</strong>
              <p>useful depending on region, audience, or location</p>
            </div>
          </div>

          <h3>Europe remains linguistically diverse</h3>
          <p>
            The European Commission reports that 59% of Europeans can have a
            conversation in at least one foreign language. At the same time,
            the number also shows that not every guest understands every
            foreign language well enough to read a menu comfortably.
          </p>
          
        </section>


        <section id="automatic" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>Why automatic translation is not always enough?</h2>
            <p>
              The main disadvantages of automatic translation or the use of 
              services from AI received for translating menus into foreign 
              languages:
            </p>

            <ul className="qrArticle__block-li">
              <li className="text__seo-li">slow menu downloading</li>
              <li className="text__seo-li">there may be different translations of the same text</li>
              <li className="text__seo-li">worse for quality control of dish descriptions</li>
            </ul>
            
            
            <p>
              Automatic translation can be a useful start. But menus are
              sensitive: regional dishes, cooking methods, allergens, meat types,
              side dishes, and cultural terms can become unclear or misleading.
            </p>

            <ul className="qrArticle__block-li">
              <li className="text__seo-li">check dish names</li>
              <li className="text__seo-li">translate allergens carefully</li>
              <li className="text__seo-li">do not hide important ingredients</li>
              <li className="text__seo-li">explain spice level and preparation</li>
              <li className="text__seo-li">make portions and sides clear</li>
              <li className="text__seo-li">show prices and options clearly</li>
            </ul>

            <p>
              A good menu translation does not need to be poetic. It needs to help
              the guest make the right decision.
            </p>
          </div>
        </section>

        <section id="qr" className="acquaintance">
          <h2>Why QR code menus make multilingual menus easier</h2>
          <p>
            With printed menus, every additional language can mean more pages,
            more layout work, more printing, and more maintenance. A digital QR
            code menu can make language handling more flexible.
          </p>
          
          <ul className="qrArticle__block-li">
            <li className="text__seo-li">Scan QR code</li>
            <li className="text__seo-li">Open menu</li>
            <li className="text__seo-li">Select language</li>
            <li className="text__seo-li">Understand dishes</li>
            <li className="text__seo-li">Order with confidence</li>
          </ul>
          
          <p>
            When guests can choose their own language, the team does not need to
            translate every detail at the table. Staff can focus more on service
            and hospitality.
          </p>
        </section>

        <section id="mistakes" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>Common multilingual menu mistakes</h2>
              <div className="multiMenuArticle__mistakes">
            <div>
              <h3>1. Translating only dish names</h3>
              <p>
                The name is often not enough. Guests may also need ingredients,
                preparation, or side dish information.
              </p>
            </div>

            <div>
              <h3>2. Translating allergens inaccurately</h3>
              <p>
                Allergens and important notes need extra care in every language.
              </p>
            </div>

            <div>
              <h3>3. Too many languages on paper</h3>
              <p>
                Printed menus can quickly become cluttered when every language
                appears on every page.
              </p>
            </div>

            <div>
              <h3>4. Not reviewing translations</h3>
              <p>
                Automatic translation can help, but menu texts should still be
                checked.
              </p>
            </div>

            <div>
              <h3>5. Language switching is hard to find</h3>
              <p>
                If guests cannot find the language option, multilingual content
                does not help much.
              </p>
            </div>

            <div>
              <h3>6. Prices and options are unclear</h3>
              <p>
                Side dishes, extras, and additional prices must be clear in
                every language.
              </p>
            </div>
          </div>
          </div>
        </section>

        <SeoWorkSteps lang="en" />

        <section id="faq" className="articls">
          <h2>Frequently asked questions</h2>
          <div className="qrArticle__faq">
            {faqLinks.map((item) => (
              <details key={item.to}>
                <summary>{item.question}</summary>

                <p>{item.answer}</p>

                <p>
                  <Link to={item.to}>{item.linkText}</Link>
                </p>
              </details>
            ))}
          </div>
        </section>

        
      </article>
    </main>
  );
}